import { Box, Button, TextField, FormLabel, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import clockIcon from '../assets/icons/clock.svg';
import SvgIcon from './SvgIcon';

export type ValidationFormProps = {
  onSubmit: (otpCode: string) => Promise<void>;
  retrySendSms: () => Promise<void>;
  timeLeft: number;
  setTimeLeft: (timeLeft: number) => void;
};

export default function ValidationForm({
  onSubmit,
  retrySendSms,
  timeLeft,
  setTimeLeft,
}: ValidationFormProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [otpCode, setOtpCode] = useState<string>('');
  const isRegisterValidation = pathname === '/register';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtpCode(e.target.value);
  };

  useEffect(() => {
    if (timeLeft === 0) return;

    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const formatTime = () => {
    const minutes = Math.floor(timeLeft / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (timeLeft % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSubmit = (e: React.SyntheticEvent) => {
    otpCode && onSubmit(otpCode);
    e.preventDefault();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
        <FormLabel htmlFor="otpCode">
          {t('validationForm.validationLabel')}
        </FormLabel>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SvgIcon
            src={clockIcon}
            style={{
              fill: '#2EB0A2',
              width: 12,
              height: 12,
            }}
          />
          <Typography sx={{ color: '#2CBCB1' }} ml={0.5} mt={0.5} mr={3}>
            {formatTime()}
          </Typography>
        </Box>
      </Box>
      <TextField
        type="text"
        // inputProps={{
        //   maxLength: 6,
        //   inputmode: 'numeric',
        //   pattern: '[0-9\\s]{13,19}',
        // }}
        name="otpCode"
        value={otpCode}
        onChange={handleChange}
        color="secondary"
        size="small"
        disabled={timeLeft === 0}
        required
        sx={{
          width: '100%',
          textAlign: 'center',
        }}
      />

      {isRegisterValidation ? (
        <Button
          sx={{ mt: 3, width: '100%' }}
          variant="contained"
          color={timeLeft === 0 || otpCode.length < 6 ? 'inherit' : 'secondary'}
          type="submit"
          disabled={timeLeft === 0 || otpCode.length < 6}
        >
          {t('validationForm.approveButton')}
        </Button>
      ) : (
        <>
          <Button
            sx={{ mt: 3, width: '100%' }}
            color={timeLeft === 0 || otpCode.length < 6 ? 'inherit' : 'primary'}
            type="submit"
            variant="contained"
            disabled={timeLeft === 0 || otpCode.length < 6}
          >
            {t('validationForm.pwResetButton')}
          </Button>
        </>
      )}
      <Button
        sx={{ mt: 3, width: '100%' }}
        variant="contained"
        color={timeLeft > 0 ? 'inherit' : 'secondary'}
        onClick={() => {
          retrySendSms();
          setOtpCode('');
        }}
        disabled={timeLeft > 0}
      >
        {t('validationForm.retrySubmitSmsButton')}
      </Button>
    </form>
  );
}
